import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BaseTableMapper } from '../../tinea-components/table/mapper/BaseTableMapper';
import { TableModel } from '../../tinea-components/table/models/input/table-model';
import { TableTh } from '../../tinea-components/table/models/input/table-th';
import { ValueCell } from '../../tinea-components/table/models/input/value-cell';
import { TableRows } from '../../tinea-components/table/models/input/table-rows';
import { TableTd } from '../../tinea-components/table/models/input/table-td';
import { CurrentInhibitionInfosList } from 'src/swagger-gen/gnvert-secured/models/current-inhibition-infos-list';
import { DEFAULT_DISPLAY_NO_DATA, DEFAULT_STATION_CSS_CLASS_CONFIG } from 'src/app/shared/constants/table.constants';
import { calculateDuration } from '@gnv-utils';

@Injectable({
  providedIn: "root",
})
export class TableInhibitionMonitoringList extends BaseTableMapper {

  constructor(private readonly translate: TranslateService) {
    super();
    this.table = new TableModel();
  }

  /**
   * Initializes the table model with data.
   * @param monitoringRepoPagedList List of external API monitoring data.
   * @returns The configured TableModel instance.
   */
  init(monitoringRepoPagedList: CurrentInhibitionInfosList): TableModel {
    this.table = new TableModel();
    this.initConfiguration();
    this.initHeader();
    this.initDataRow(monitoringRepoPagedList);
    this.table.paginationEnabled = true;

    return this.table;
  }

  /**
   * Configures general table settings.
   */
  private initConfiguration(): void {
    this.table.configuration.cssClass = DEFAULT_STATION_CSS_CLASS_CONFIG;
    this.table.configuration.noDataRowMsg =
      this.translate.instant("error.no_result");
  }

  /**
   * Initializes table headers.
   */
  private initHeader(): void {
    const headers: TableTh[] = [
      this.createHeaderCell('COLUMN_STATION_NAME'),
      this.createHeaderCell('COLUMN_STATION_CODE'),
      this.createHeaderCell('COLUMN_TAG_NAME'),
      this.createHeaderCell('COLUMN_TAG_ALIAS'),
      this.createHeaderCell('COLUMN_INHIBITION_DATE'),
      this.createHeaderCell('COLUMN_INHIBITION_DURATION'),
    ];

    this.setHeaderCells(headers);
  }

  /**
   * Populates the table with rows based on the monitoring data.
   * @param monitoringData List of Inhibitions.
   */
  private initDataRow(monitoringData: CurrentInhibitionInfosList): void {
    monitoringData?.forEach((inhibition) => {
      const tableRow = new TableRows(inhibition);

      // td - Station name
      const stationNameCell = this.createTableCell(inhibition.stationName, "STRING");

      // td - station code
      const stationCodeCell = this.createTableCell(inhibition.stationCode, "STRING");

      // td - default name
      const tagNameCell = this.createTableCell(inhibition.tagName, "STRING");

      // td - default alias
      const tagAliasCell = this.createTableCell(inhibition.tagAlias, "STRING");

       // td - inhibition date
      const inhibitionDateCell = this.createTableCell(
        inhibition.datetime,
        "DATE"
      );

      // td - inhibition duration
      const inhibitionDurationCell = this.createTableCell(calculateDuration(inhibition.datetime), "STRING");

      tableRow.cells = [
        stationNameCell,
        stationCodeCell,
        tagNameCell,
        tagAliasCell,
        inhibitionDateCell,
        inhibitionDurationCell,
      ];

      this.table.dataRow.push(tableRow);
    });
  }

  /**
   * Creates a table header cell.
   * @param columnId The column identifier.
   * @returns A configured TableTh instance.
   */
  private createHeaderCell(columnId: string): TableTh {
    const header = new TableTh();
    header.columnId = columnId;

    const valueCell = new ValueCell();
    valueCell.displayedValue = this.translateValue(
      `inhibitionMonitoring.table.${columnId}`
    ) as string;

    header.valueCells.push(valueCell);
    return header;
  }

  /**
   * Creates a table data cell.
   * @param value The value to display.
   * @param dataType (Optional) The data type (default: STRING).
   * @returns A configured TableTd instance.
   */
  private createTableCell(
    value: string | undefined,
    dataType: "STRING" | "DATE" | "NUMBER" | "ENUM" = "STRING",
    tooltip?: string
  ): TableTd {
    const cell = new TableTd();
    const valueCell = new ValueCell();

    valueCell.displayedValue = value ?? DEFAULT_DISPLAY_NO_DATA;
    valueCell.dataType = dataType;
    valueCell.tooltip = tooltip || "";

    cell.valueCells.push(valueCell);

    return cell;
  }

  /**
   * Translates a given value if available.
   * @param value The value key to translate.
   * @returns The translated string or null if value is empty.
   */
  private translateValue(value: string): string | null {
    return value ? this.translate.instant(value) : null;
  }
}
